import { l } from '@js/utils/toolbox';

/**
 * Dialog class for displaying a dialog with accept or cancel options.
 *
 * Usage:
 * const dialog = new Dialog('Title', 'Message', {
 *   onAccept: () => console.log('accepted'),
 *   onCancel: () => console.log('cancelled')
 * });
 *
 * const result = await dialog.show();
 * if (result === 'accept') { ... }
 * if (result === 'cancel') { ... }
 */
export class Dialog {
	/**
   * @param {string} title
   * @param {string} text
   * @param {Object} options
   * @param {boolean} [options.singleButton=false]
   * @param {boolean} [options.cancelOnClickout=false]
   * @param {Array<HTMLElement>} [options.additionalElements]
   * @param {Function} [options.onAccept]
   * @param {Function} [options.onCancel]
   */
	constructor (title, text, options = { cancelOnClickout: true }) {
		this.title = title;
		this.text = text;
		this.options = options;

		this.dialog = null;
		this._isSettled = false;
		this._resolve = null;

		this.createDialog();
	}

	createDialog () {
		this.dialog = document.createElement('dialog');
		this.dialog.className = 'dialog hidden';

		const dialogContent = document.createElement('div');
		dialogContent.className = 'dialog-content';

		const dialogTitle = document.createElement('h1');
		dialogTitle.className = 'dialog-title';
		dialogTitle.innerText = this.title;

		const dialogText = document.createElement('p');
		dialogText.className = 'dialog-text';
		dialogText.innerText = this.text;

		const dialogFooter = document.createElement('div');
		dialogFooter.className = 'dialog-footer';

		if (!this.options?.singleButton) {
			const cancelButton = document.createElement('button');
			cancelButton.className = 'dialog-btn abBtn abBtn--flex abBtn--whiteBlue';
			cancelButton.innerText = l('global.simplePhrases.abort');
			cancelButton.addEventListener('click', this.cancel.bind(this));
			dialogFooter.appendChild(cancelButton);
		}

		const acceptButton = document.createElement('button');
		acceptButton.className = 'dialog-btn abBtn abBtn--flex abBtn--blueWhite';
		acceptButton.innerText = l('global.simplePhrases.ok');
		acceptButton.addEventListener('click', this.accept.bind(this));
		dialogFooter.appendChild(acceptButton);

		this.dialog.addEventListener('click', (event) => {
			if (!event.target.closest('.dialog-content')) {
				if (this.options.cancelOnClickout) {
					this.cancel();
				} else {
					this.accept();
				}
			}
		});

		this.dialog.addEventListener('cancel', (event) => {
			// Handles Esc key / native dialog cancel behavior
			event.preventDefault();
			this.cancel();
		});

		dialogContent.appendChild(dialogTitle);
		dialogContent.appendChild(dialogText);

		if (this.options.additionalElements?.length) {
			for (const additionalElement of this.options.additionalElements) {
				dialogContent.append(additionalElement);
			}
		}

		dialogContent.appendChild(dialogFooter);
		this.dialog.appendChild(dialogContent);
		document.body.appendChild(this.dialog);
	}

	show () {
		document.body.classList.add('modal-open');
		this.dialog.showModal();
		this.dialog.classList.remove('hidden');

		return new Promise((resolve) => {
			this._resolve = resolve;
		});
	}

	accept () {
		if (this._isSettled) {
			return;
		}
		this._isSettled = true;

		if (typeof this.options.onAccept === 'function') {
			this.options.onAccept();
		}

		if (this._resolve) {
			this._resolve('accept');
		}

		this.destroy();
	}

	cancel () {
		if (this._isSettled) {
			return;
		}
		this._isSettled = true;

		this.dialog.classList.add('hidden');

		if (typeof this.options.onCancel === 'function') {
			this.options.onCancel();
		}

		if (this._resolve) {
			this._resolve('cancel');
		}

		setTimeout(() => this.destroy(), 300);
	}

	destroy () {
		document.body.classList.remove('modal-open');

		if (this.dialog) {
			this.dialog.close?.();
			this.dialog.style.display = 'none';
			this.dialog.remove();
			this.dialog = null;
		}
	}
}
